const VFB_SWITCH_DURATION = 0.3;
const VFB_SWIPE_DISTANCE = 40;
const VFB_SWIPE_AXIS_RATIO = 1.5;
const VFB_GSAP_WAIT_FRAMES = 300;
const VFB_ENTRY_VIEWPORT_RATIO = 0.75;

/**
 * Variant switcher + feature breakdown.
 *
 * Tab switching, keyboard support and the sliding indicator work as soon as this
 * module runs. GSAP is an optional upgrade that replaces the CSS cross-fade with
 * a scroll-triggered entry sequence and a richer switch transition.
 */
class VariantFeatureBreakdown extends HTMLElement {
  #controller;
  #gsap;
  #matchMedia;
  #switchTimeline;
  #resizeObserver;
  #gsapFrame;
  #indicatorFrame;
  #gsapWaitFrames = 0;
  #activeIndex = 0;
  #pointerStart = null;
  #tablist;
  #tabbar;
  #indicator;
  #stage;
  #copy;
  #title;
  #description;
  #tabs = [];
  #panels = [];

  connectedCallback() {
    if (this.#controller) return;

    this.#stage = this.querySelector('[data-vfb-stage]');
    this.#tablist = this.querySelector('[data-vfb-tablist]');
    this.#tabbar = this.querySelector('[data-vfb-tabbar]');
    this.#indicator = this.querySelector('[data-vfb-indicator]');
    this.#copy = this.querySelector('[data-vfb-copy]');
    this.#title = this.querySelector('[data-vfb-title]');
    this.#description = this.querySelector('[data-vfb-description]');
    this.#tabs = [...this.querySelectorAll('[data-vfb-tab]')];
    this.#panels = [...this.querySelectorAll('[data-vfb-panel]')];

    if (!this.#panels.length) return;

    this.#controller = new AbortController();
    this.#activeIndex = Math.max(
      0,
      this.#panels.findIndex((panel) => panel.hasAttribute('data-vfb-active'))
    );

    this.#bindEvents();
    this.classList.add('is-enhanced');
    this.#syncIndicator({ animate: false });
    this.#syncTabbarOverflow();

    this.#gsapWaitFrames = 0;
    this.#waitForGsap();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = undefined;

    if (this.#gsapFrame) cancelAnimationFrame(this.#gsapFrame);
    this.#gsapFrame = undefined;
    if (this.#indicatorFrame) cancelAnimationFrame(this.#indicatorFrame);
    this.#indicatorFrame = undefined;

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;

    this.#switchTimeline?.kill();
    this.#switchTimeline = undefined;
    this.#matchMedia?.revert();
    this.#matchMedia = undefined;
    this.#gsap = undefined;

    this.classList.remove('is-enhanced', 'is-measured', 'is-motion');
  }

  get #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #bindEvents() {
    const { signal } = this.#controller;

    if (this.#tablist) {
      this.#tablist.addEventListener('click', this.#onTablistClick, { signal });
      this.#tablist.addEventListener('keydown', this.#onTablistKeydown, { signal });

      this.#tablist.addEventListener('scroll', this.#syncTabbarOverflow, { signal, passive: true });

      this.#resizeObserver = new ResizeObserver(() => {
        this.#scheduleIndicatorSync();
        this.#syncTabbarOverflow();
      });
      this.#resizeObserver.observe(this.#tablist);
    }

    this.#tabbar?.querySelector('[data-vfb-tabbar-previous]')?.addEventListener('click', this.#onTabbarPrevious, { signal });
    this.#tabbar?.querySelector('[data-vfb-tabbar-next]')?.addEventListener('click', this.#onTabbarNext, { signal });

    if (this.#stage && this.hasAttribute('data-vfb-swipe')) {
      this.#stage.addEventListener('pointerdown', this.#onPointerDown, { signal, passive: true });
      this.#stage.addEventListener('pointerup', this.#onPointerUp, { signal, passive: true });
      this.#stage.addEventListener('pointercancel', this.#onPointerCancel, { signal, passive: true });
    }

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
    }
  }

  #scheduleIndicatorSync() {
    if (this.#indicatorFrame) return;

    this.#indicatorFrame = requestAnimationFrame(() => {
      this.#indicatorFrame = undefined;
      this.#syncIndicator({ animate: false });
    });
  }

  /**
   * `offsetLeft`/`offsetTop` are measured from the tablist's padding edge, which is
   * exactly where the absolutely positioned indicator starts, so no offset math is
   * needed and the value stays correct while the tablist scrolls horizontally.
   */
  #syncIndicator({ animate = true } = {}) {
    const activeTab = this.#tabs[this.#activeIndex];
    if (!this.#indicator || !activeTab) return;

    if (!animate) this.classList.remove('is-measured');

    this.#indicator.style.transform = `translate3d(${activeTab.offsetLeft}px, 0, 0)`;
    this.#indicator.style.inlineSize = `${activeTab.offsetWidth}px`;

    if (!animate) {
      requestAnimationFrame(() => this.classList.add('is-measured'));
    }
  }

  #onTablistClick = (event) => {
    const tab = event.target.closest('[data-vfb-tab]');
    if (!tab || !this.#tablist.contains(tab)) return;

    this.#activate(Number(tab.dataset.vfbIndex));
  };

  #onTablistKeydown = (event) => {
    const tab = event.target.closest('[data-vfb-tab]');
    if (!tab || !this.#tablist.contains(tab)) return;

    const lastIndex = this.#tabs.length - 1;
    const isRtl = getComputedStyle(this).direction === 'rtl';
    let nextIndex;

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = isRtl ? this.#activeIndex - 1 : this.#activeIndex + 1;
        break;
      case 'ArrowLeft':
        nextIndex = isRtl ? this.#activeIndex + 1 : this.#activeIndex - 1;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = lastIndex;
        break;
      default:
        return;
    }

    if (nextIndex < 0) nextIndex = lastIndex;
    if (nextIndex > lastIndex) nextIndex = 0;

    event.preventDefault();
    this.#activate(nextIndex, { focusTab: true });
  };

  #onPointerDown = (event) => {
    if (event.pointerType === 'mouse' || !event.isPrimary) return;

    this.#pointerStart = { x: event.clientX, y: event.clientY, id: event.pointerId };

    // Keeps pointerup/pointercancel on the stage even when the finger leaves it.
    // `touch-action: pan-y` still lets the browser cancel the gesture to scroll.
    this.#stage.setPointerCapture?.(event.pointerId);
  };

  #onPointerUp = (event) => {
    const start = this.#pointerStart;
    this.#pointerStart = null;

    if (!start || start.id !== event.pointerId) return;

    const deltaX = event.clientX - start.x;
    const deltaY = event.clientY - start.y;

    if (Math.abs(deltaX) < VFB_SWIPE_DISTANCE) return;
    if (Math.abs(deltaX) < Math.abs(deltaY) * VFB_SWIPE_AXIS_RATIO) return;

    const isRtl = getComputedStyle(this).direction === 'rtl';
    const backwards = isRtl ? deltaX < 0 : deltaX > 0;

    this.#activate(this.#activeIndex + (backwards ? -1 : 1));
  };

  #onPointerCancel = () => {
    this.#pointerStart = null;
  };

  #onBlockSelect = (event) => {
    const panel = this.#panels.find((item) => item.contains(event.target) || item === event.target);
    if (!panel) return;

    this.#activate(Number(panel.dataset.vfbIndex));
  };

  #activate(index, { focusTab = false } = {}) {
    if (!Number.isInteger(index)) return;
    if (index < 0 || index > this.#panels.length - 1) return;

    const previousIndex = this.#activeIndex;
    const activeTab = this.#tabs[index];

    if (focusTab) activeTab?.focus({ preventScroll: true });
    if (index === previousIndex) return;

    this.#activeIndex = index;

    this.#tabs.forEach((tab, tabIndex) => {
      const isActive = tabIndex === index;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.tabIndex = isActive ? 0 : -1;
    });

    this.#panels.forEach((panel, panelIndex) => {
      panel.toggleAttribute('data-vfb-active', panelIndex === index);
    });

    this.#syncFeatureCopy(index);

    this.#syncIndicator();

    this.#scrollTabIntoView(activeTab);

    if (this.#gsap) this.#animateSwitch(previousIndex, index);
  }

  /**
   * Keeps the copy under the tabs on the active variant.
   * Description only follows the variant when a panel carries its own text.
   * @param {number} index
   */
  #syncFeatureCopy(index) {
    const activeTab = this.#tabs[index];
    const descriptionSource = this.#panels[index]?.querySelector('[data-vfb-feature-description]');

    if (this.#title && activeTab?.dataset.vfbFeatureTitle) {
      this.#title.textContent = activeTab.dataset.vfbFeatureTitle;
    }

    if (!this.#description || !descriptionSource) return;

    const text = descriptionSource.textContent || '';
    this.#description.replaceChildren();
    text.split('\n').forEach((line, lineIndex) => {
      if (lineIndex > 0) this.#description.append(document.createElement('br'));
      if (line) this.#description.append(document.createTextNode(line));
    });
    this.#description.hidden = text.trim() === '';
  }

  /**
   * Shows the trailing scroll hint only while more tabs sit off-screen.
   */
  #syncTabbarOverflow = () => {
    if (!this.#tabbar || !this.#tablist) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.#tablist;
    const isOverflowing = scrollWidth - clientWidth > 1;
    const isScrolledStart = scrollLeft <= 1;
    const isScrolledEnd = scrollLeft + clientWidth >= scrollWidth - 1;

    this.#tabbar.classList.toggle('is-overflowing', isOverflowing);
    this.#tabbar.classList.toggle('is-scrolled-start', isScrolledStart);
    this.#tabbar.classList.toggle('is-scrolled-end', isScrolledEnd);

    const previous = this.#tabbar.querySelector('[data-vfb-tabbar-previous]');
    const next = this.#tabbar.querySelector('[data-vfb-tabbar-next]');
    if (previous instanceof HTMLButtonElement) {
      previous.disabled = !isOverflowing || isScrolledStart;
    }
    if (next instanceof HTMLButtonElement) {
      next.disabled = !isOverflowing || isScrolledEnd;
    }
  };

  #tabbarHintWidth(hint) {
    if (!(hint instanceof HTMLElement)) return 0;
    return getComputedStyle(hint).display !== 'none' ? hint.offsetWidth : 0;
  }

  #tablistScrollStep() {
    if (!this.#tablist) return 0;
    const tab = this.#tabs[0];
    if (tab) {
      const styles = getComputedStyle(this.#tablist);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
      return tab.offsetWidth + gap;
    }
    return Math.round(this.#tablist.clientWidth * 0.6);
  }

  #onTabbarPrevious = () => {
    if (!this.#tablist) return;
    this.#tablist.scrollBy({
      left: -this.#tablistScrollStep(),
      behavior: this.#prefersReducedMotion ? 'auto' : 'smooth'
    });
  };

  #onTabbarNext = () => {
    if (!this.#tablist) return;
    this.#tablist.scrollBy({
      left: this.#tablistScrollStep(),
      behavior: this.#prefersReducedMotion ? 'auto' : 'smooth'
    });
  };

  #scrollTabIntoView(tab) {
    if (!this.#tablist || !tab) return;

    const tabRect = tab.getBoundingClientRect();
    const tablistRect = this.#tablist.getBoundingClientRect();
    const startHint = this.#tabbar?.querySelector('.variant-feature-breakdown__tabbar-hint--start');
    const endHint = this.#tabbar?.querySelector('.variant-feature-breakdown__tabbar-hint--end');
    const leftLimit = tablistRect.left + this.#tabbarHintWidth(startHint);
    const rightLimit = tablistRect.right - this.#tabbarHintWidth(endHint);
    let scrollDelta = 0;

    if (tabRect.left < leftLimit) {
      scrollDelta = tabRect.left - leftLimit;
    } else if (tabRect.right > rightLimit) {
      scrollDelta = tabRect.right - rightLimit;
    }

    if (Math.abs(scrollDelta) <= 1) return;

    this.#tablist.scrollBy({
      left: scrollDelta,
      behavior: this.#prefersReducedMotion ? 'auto' : 'smooth'
    });
  }

  #animateSwitch(previousIndex, nextIndex) {
    const gsap = this.#gsap;
    const incomingPanel = this.#panels[nextIndex];
    const outgoingPanel = this.#panels[previousIndex];

    this.#switchTimeline?.kill();

    this.#panels.forEach((panel) => {
      if (panel === incomingPanel || panel === outgoingPanel) return;
      gsap.set(panel, { autoAlpha: 0, clearProps: 'zIndex' });
    });

    if (this.#prefersReducedMotion) {
      if (outgoingPanel) gsap.set(outgoingPanel, { autoAlpha: 0, clearProps: 'zIndex' });
      if (incomingPanel) gsap.set(incomingPanel, { autoAlpha: 1, clearProps: 'zIndex' });
      return;
    }

    const timeline = gsap.timeline({
      defaults: { duration: VFB_SWITCH_DURATION, ease: 'power2.out' }
    });

    if (outgoingPanel) {
      timeline.set(outgoingPanel, { autoAlpha: 1, zIndex: 1 }, 0);
    }

    if (incomingPanel) {
      timeline.set(incomingPanel, { autoAlpha: 0, zIndex: 2 }, 0);
      timeline.to(incomingPanel, { autoAlpha: 1 }, 0);
    }

    if (outgoingPanel) {
      timeline.set(outgoingPanel, { autoAlpha: 0 }, '>');
    }

    const transitionPanels = [outgoingPanel, incomingPanel].filter(Boolean);
    timeline.set(transitionPanels, { clearProps: 'zIndex' }, '>');

    this.#switchTimeline = timeline;
  }

  #waitForGsap = () => {
    if (!this.#controller || this.#controller.signal.aborted) return;

    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger || gsap?.core?.globals?.().ScrollTrigger;

    if (gsap && ScrollTrigger) {
      this.#setupMotion(gsap, ScrollTrigger);
      return;
    }

    this.#gsapWaitFrames += 1;
    if (this.#gsapWaitFrames >= VFB_GSAP_WAIT_FRAMES) return;

    this.#gsapFrame = requestAnimationFrame(this.#waitForGsap);
  };

  #setupMotion(gsap, ScrollTrigger) {
    gsap.registerPlugin(ScrollTrigger);

    this.#gsap = gsap;
    this.classList.add('is-motion');

    // GSAP took over opacity/visibility; mirror the server-rendered state as inline values.
    this.#panels.forEach((panel, index) => {
      gsap.set(panel, { autoAlpha: index === this.#activeIndex ? 1 : 0 });
    });

    // A late-loading GSAP must not hide content the visitor is already looking at.
    const isAlreadyInView =
      this.getBoundingClientRect().top < window.innerHeight * VFB_ENTRY_VIEWPORT_RATIO;

    this.#matchMedia = gsap.matchMedia();
    this.#matchMedia.add(
      { reduceMotion: '(prefers-reduced-motion: reduce)' },
      (context) => {
        if (context.conditions.reduceMotion || isAlreadyInView) return undefined;

        const activePanel = this.#panels[this.#activeIndex];
        const activeDiscs = activePanel ? [...activePanel.querySelectorAll('[data-vfb-disc]')] : [];
        const copyLines = this.#copy ? [...this.#copy.children] : [];

        const timeline = gsap.timeline({
          defaults: { ease: 'power2.out' },
          scrollTrigger: {
            id: `variant-feature-breakdown-${this.dataset.sectionId}`,
            trigger: this,
            start: `top ${VFB_ENTRY_VIEWPORT_RATIO * 100}%`,
            once: true
          }
        });

        if (this.#stage) {
          timeline.from(this.#stage, { autoAlpha: 0, y: 20, duration: 0.7 }, 0);
        }

        // A single spin-up per disc. `clearProps` drops the inline matrix afterwards so
        // the disc is back to its plain, compositor-free resting state.
        if (activeDiscs.length) {
          timeline.fromTo(
            activeDiscs,
            { rotation: 0 },
            {
              rotation: 360,
              duration: 1.4,
              ease: 'power2.inOut',
              stagger: 0.12,
              clearProps: 'transform'
            },
            0.3
          );
        }

        if (this.#tablist) {
          timeline.from(this.#tablist, { autoAlpha: 0, y: 12, duration: 0.45 }, 0.5);
        }

        if (copyLines.length) {
          timeline.from(copyLines, { autoAlpha: 0, y: 12, duration: 0.45, stagger: 0.08 }, 0.6);
        }

        return () => {
          timeline.scrollTrigger?.kill();
          timeline.kill();
        };
      },
      this
    );
  }
}

if (!customElements.get('variant-feature-breakdown')) {
  customElements.define('variant-feature-breakdown', VariantFeatureBreakdown);
}
